import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { toastr } from "react-redux-toastr";
import Loading from "components/Loading";
import { payflowIntegrationService } from "services";

const LOG_TYPE_VARIANTS = {
	ERROR: "error",
	WARNING: "warning",
	WARN: "warning",
	INFO: "info",
	SUCCESS: "success",
};

const PayflowErrorLogModal = ({ employeeId, handleClose }) => {
	const { t } = useTranslation();
	const [loading, setLoading] = useState(true);
	const [logs, setLogs] = useState([]);

	useEffect(() => {
		let active = true;

		const loadLogs = async () => {
			setLoading(true);
			try {
				const response = await payflowIntegrationService.getSyncLogs(
					employeeId != null ? { employee_id: employeeId } : {}
				);
				if (!active) return;

				if (response && response.ok === false) {
					toastr.error(
						t("common.error"),
						response.errors?.[0]?.description ||
							t("integrations.payflow.logs.errorLoading")
					);
					setLogs([]);
					return;
				}

				const data = Array.isArray(response?.data)
					? response.data
					: Array.isArray(response)
						? response
						: [];
				setLogs(data);
			} finally {
				if (active) setLoading(false);
			}
		};

		loadLogs();

		return () => {
			active = false;
		};
	}, [employeeId, t]);

	const getTypeLabel = (type) => {
		const normalized = String(type || "").toUpperCase();
		if (normalized === "ERROR") return t("integrations.payflow.logs.typeError");
		if (normalized === "WARNING" || normalized === "WARN") {
			return t("integrations.payflow.logs.typeWarning");
		}
		return type || "-";
	};

	const getTypeClass = (type) => {
		const normalized = String(type || "").toUpperCase();
		return LOG_TYPE_VARIANTS[normalized] || "info";
	};

	if (loading) {
		return (
			<div className="payflow-log-modal">
				<Loading />
			</div>
		);
	}

	return (
		<div className="payflow-log-modal">
			<p className="payflow-log-modal__subtitle">
				{t("integrations.payflow.logs.subtitle")}
			</p>

			<div className="table-responsive">
				<table className="table payflow-log-table">
					<thead>
						<tr>
							<th>{t("integrations.payflow.logs.dateTime")}</th>
							<th>{t("integrations.payflow.logs.type")}</th>
							<th>{t("integrations.payflow.logs.message")}</th>
						</tr>
					</thead>
					<tbody>
						{logs.length === 0 ? (
							<tr>
								<td colSpan={3}>{t("integrations.payflow.logs.empty")}</td>
							</tr>
						) : (
							logs.map((log) => (
								<tr key={log.id}>
									<td>
										{log.created
											? moment.utc(log.created).local().format("D MMMM YYYY - HH:mm")
											: "-"}
									</td>
									<td>
										<span
											className={`payflow-log-badge payflow-log-badge--${getTypeClass(log.type)}`}
										>
											{getTypeLabel(log.type)}
										</span>
									</td>
									<td>{log.description || "-"}</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			<div className="payflow-log-modal__footer">
				<button
					type="button"
					className="btn btn-outline-primary"
					onClick={handleClose}
				>
					{t("common.close")}
				</button>
			</div>
		</div>
	);
};

export default PayflowErrorLogModal;
